import { ValidationError } from '@domain/errors';
import type { AppUser, AppUserDraft } from '@domain/entities';
import type { AppUserQuery, AppUserRepository } from '@domain/repositories';
import type { AccountStatus, Page } from '@domain/value-objects';
import type { AccessPolicy, FileExporter } from '../../ports';
import type { UseCase } from '../use-case';

export class CreateAppUser implements UseCase<AppUserDraft, AppUser> {
  constructor(private readonly repo: AppUserRepository) {}

  async execute(draft: AppUserDraft): Promise<AppUser> {
    const fields: Record<string, string> = {};
    if (!draft.email.trim() && !draft.phone.trim()) {
      fields.email = 'Un email ou un téléphone est requis';
    }
    if (Object.keys(fields).length > 0) throw new ValidationError('Formulaire incomplet', fields);
    return this.repo.create({
      ...draft,
      name: draft.name.trim(),
      email: draft.email.trim().toLowerCase(),
    });
  }
}

export class SearchAppUsers implements UseCase<AppUserQuery, Page<AppUser>> {
  constructor(private readonly repo: AppUserRepository) {}
  execute(query: AppUserQuery): Promise<Page<AppUser>> {
    return this.repo.search(query);
  }
}

export interface UpdateAppUserInput {
  id: string;
  draft: AppUserDraft;
}

export class UpdateAppUser implements UseCase<UpdateAppUserInput, AppUser> {
  constructor(private readonly repo: AppUserRepository) {}

  async execute({ id, draft }: UpdateAppUserInput): Promise<AppUser> {
    const fields: Record<string, string> = {};
    if (!draft.name.trim()) fields.name = 'Le nom est requis';
    if (!draft.email.trim()) fields.email = "L'email est requis";
    if (Object.keys(fields).length > 0) {
      throw new ValidationError('Formulaire incomplet', fields);
    }
    return this.repo.update(id, {
      ...draft,
      name: draft.name.trim(),
      email: draft.email.trim().toLowerCase(),
    });
  }
}

export interface SetAppUserStatusInput {
  id: string;
  status: AccountStatus;
}

/** Désactivation / réactivation d'un compte : réservé au super-admin. */
export class SetAppUserStatus implements UseCase<SetAppUserStatusInput, AppUser> {
  constructor(
    private readonly repo: AppUserRepository,
    private readonly policy: AccessPolicy,
  ) {}

  async execute({ id, status }: SetAppUserStatusInput): Promise<AppUser> {
    this.policy.assertSuperAdmin('modifier le statut d’un compte');
    return this.repo.setStatus(id, status);
  }
}

export class ExportAppUsers implements UseCase<void, number> {
  constructor(
    private readonly repo: AppUserRepository,
    private readonly exporter: FileExporter,
  ) {}

  async execute(): Promise<number> {
    const rows = await this.repo.exportAll();
    this.exporter.exportCsv('base-clients-babimams.csv', rows);
    return rows.length;
  }
}

export class DeleteAppUser implements UseCase<string, void> {
  constructor(
    private readonly repo: AppUserRepository,
    private readonly policy: AccessPolicy,
  ) {}

  async execute(id: string): Promise<void> {
    this.policy.assertSuperAdmin('supprimer un utilisateur');
    return this.repo.remove(id);
  }
}
